import {fuzzyScore} from '@github-ui/fuzzy-score/fuzzy-score'

import {DEFAULT_FILTER_PRIORITY, FILTER_KEYS} from '../constants/filter-constants'
import type {FilterQuery} from '../filter-query'
import {
  type AnyBlock,
  type ARIAFilterSuggestion,
  type FilterConfig,
  type FilterProvider,
  FilterProviderType,
  type FilterValueData,
  FilterValueType,
  type IndexedBlockValueItem,
  type MutableFilterBlock,
  type SuppliedFilterProviderOptions,
  type UnrankedARIAFilterSuggestion,
  type ValueRowProps,
} from '../types'
import {getFilterValue, getUnescapedFilterValue} from '../utils'
import {ValueIcon} from '../utils/ValueIcon'
import {AsyncFilterProvider} from './async'

const LANGUAGE_SUGGESTION_ENDPOINT = '/_filter/languages'
const LANGUAGE_VALIDATION_ENDPOINT = '/_filter/languages/validate'

export type Language = {
  name: string
  color: string
}

export class LanguageFilterProvider extends AsyncFilterProvider<Language> implements FilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.language, options)
    this.suggestionEndpoint = LANGUAGE_SUGGESTION_ENDPOINT
    this.validationEndpoint = LANGUAGE_VALIDATION_ENDPOINT
    this.type = FilterProviderType.Select
  }

  async getSuggestions(
    filterQuery: FilterQuery,
    filterBlock: AnyBlock | MutableFilterBlock,
    config: FilterConfig,
    caretIndex?: number | null,
  ) {
    return this.processSuggestions(filterQuery, filterBlock, this.#processSuggestion.bind(this), caretIndex)
  }

  override rankSuggestion(query: string, suggestion: UnrankedARIAFilterSuggestion): ARIAFilterSuggestion {
    const name = getUnescapedFilterValue(suggestion.value)
    let priority = DEFAULT_FILTER_PRIORITY

    if (query && name) priority -= fuzzyScore(query, name)

    return {...suggestion, priority}
  }

  #processSuggestion(language: Language, query: string): ARIAFilterSuggestion {
    return this.rankSuggestion(query, {
      type: FilterValueType.Value,
      displayName: language.name,
      ariaLabel: `${language.name}, ${this.displayName}`,
      value: language.name ?? '',
      iconColor: language.color?.startsWith('#') ? language.color : `#${language.color}`,
    })
  }

  override validateValue(
    filterQuery: FilterQuery,
    value: IndexedBlockValueItem,
    language: Language | null,
  ): false | Partial<IndexedBlockValueItem> | null | undefined {
    const extractedValue = getUnescapedFilterValue(value.value)
    if (!language || !extractedValue) return false

    return {
      iconColor: language.color?.startsWith('#') ? language.color : `#${language.color}`,
      value: extractedValue,
      displayName: language.name,
    }
  }

  getValueRowProps(value: FilterValueData): ValueRowProps {
    return {
      text: value.displayName ?? getFilterValue(value.value) ?? '',
      description: value.description,
      descriptionVariant: 'block',
      leadingVisual: ValueIcon({value, providerIcon: this.icon}) ?? undefined,
    }
  }
}
